import { useState, ChangeEvent, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';

import { IKnmAttachment } from 'APIPackage/KnmModule/interfaces/IKnmAttachment';
import { IChangePlanStatusModalValues } from 'CorePackage/ChangePlanStatusModalsModule/interfaces/IChangePlanStatusModalValues';
import { MB } from 'CorePackage/MainModule/constants/fileSize';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { useKnmDocumentAttachment } from 'KnmPackage/MainModule/hooks/useKnmDocumentAttachment';
import { Button } from 'UIPackage/MainModule/components/Button';
import { Modal } from 'UIPackage/ModalModule/components/Modal';
import { Textarea } from 'UIPackage/MainModule/components/Textarea';
import { UploadFile } from 'UIPackage/MainModule/components/UploadFile';
import { ModalHeader } from 'UIPackage/ModalModule/components/ModalHeader';
import { ModalBody } from 'UIPackage/ModalModule/components/ModalBody';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { addNoticeKnmFileSizeErrorBig } from 'CorePackage/NotificationModule/common/addNoticeKnmFileSizeErrorBig';

import styles from './ChangePlanStatusModal.module.scss';

const MAX_FILE_SIZE = 10 * MB;
const documentValidExtensions = ['ods', 'xls', 'xlsx', 'doc', 'docx', 'pdf'];
const documentValidExtensionsString = `.${documentValidExtensions.join(',.')}`;

interface IChangePlanStatusModalProps {
  visible: boolean;
  onApply: (values: IChangePlanStatusModalValues) => void;
  onCloseModal: () => void;
}

export const ChangePlanStatusModal = ({ visible, onApply, onCloseModal }: IChangePlanStatusModalProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const [documents, setDocuments] = useState<IKnmAttachment[]>([]);
  const [comment, setComment] = useState<string>('');
  const { uploadKnmDocumentAttachment } = useKnmDocumentAttachment();

  const handleFileSizeError = useCallback(() => {
    dispatch(addNoticeKnmFileSizeErrorBig());
  }, [dispatch]);

  const handleSaveButtonClick = useCallback(() => {
    onApply({ comment, documents });
  }, [comment, documents, onApply]);

  const handleChangeInputFile = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        const file = e.target.files[0];
        const isSizeValid = file.size <= MAX_FILE_SIZE;

        if (!isSizeValid) {
          handleFileSizeError();
        } else {
          const attachment = await uploadKnmDocumentAttachment(file);

          if (attachment) {
            setDocuments(prev => [...prev, attachment]);
          }
        }

        e.target.value = '';
      }
    },
    [handleFileSizeError, uploadKnmDocumentAttachment],
  );

  const makeRemoveAttachmentHandler = (index: number) => () => {
    const filteredDocuments = documents.filter((el, idx) => idx !== index);

    setDocuments(filteredDocuments);
  };

  const handleChangeTextValue = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => {
    setComment(e.target.value);
  }, []);

  const handleAfterCloseModal = useCallback(() => {
    setComment('');
    setDocuments([]);
  }, []);

  return (
    <Modal visible={visible} onClose={onCloseModal} afterClose={handleAfterCloseModal}>
      <ModalHeader title="Смена статуса" sizeText="small" color="black" />
      <ModalBody size="content" border="bottom">
        <Row>
          <FieldBlock label="Комментарий" color="black">
            <Textarea
              inputClassName={styles.CommentFieldInput}
              id="comment"
              name="comment"
              value={comment}
              resize="none"
              onChange={handleChangeTextValue}
            />
          </FieldBlock>
        </Row>
        <Row>
          <FieldBlock
            label="Документы"
            placementLabel="left"
            button={
              <label className="shared-link" htmlFor="documentFiles">
                Добавить
              </label>
            }
          >
            <input
              id="documentFiles"
              name="documentFiles"
              type="file"
              accept={documentValidExtensionsString}
              style={{ display: 'none' }}
              onChange={handleChangeInputFile}
            />
          </FieldBlock>
        </Row>

        {documents.length > 0 &&
          documents.map((file, idx) => (
            <Row key={idx}>
              <UploadFile
                fileName={file.fileName || ''}
                fileSize={file.fileSize || 0}
                progress={file.dateCreate ? 100 : 0}
                onRemove={makeRemoveAttachmentHandler(idx)}
              />
            </Row>
          ))}
      </ModalBody>
      <ModalActions>
        <Button
          onClick={handleSaveButtonClick}
          text="Сохранить"
          id="saveButton"
          color="primary"
          size="medium"
          type="button"
        />
      </ModalActions>
    </Modal>
  );
};
